/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react/macro";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { ChevronDown } from "tabler-icons-react";
import {
  useCityInfoQuery,
  useForestageCityInfoUpdateDateQuery,
  useHistoricalMonumentCountQuery,
  useHospitalBedCountQuery,
  useHospitalCountQuery,
  useHotelCountQuery,
  useLandTradeQuery,
  usePopulationQuery,
  useWeatherForecastQuery,
} from "services/api";
import Logo from "images/LOGO.svg";
import IconClose from "images/icon-close.svg";
import IconWeather from "images/icon-weather.svg";
import IconInfoClose from "images/icon-info-close.svg";

import LineChart from "./LineChart";
import BarChart from "./BarChart";
import CityInfoUnit from "./CityInfoUnit";
import CityInfoUnitTitle from "./CityInfoUnitTitle";
import { DISTRICT } from "./district";

const getPercentString = (target?: number, total?: number) => {
  if (total === undefined || total === 0 || target === undefined) {
    return "50%";
  }

  return `${+(target / total).toFixed(2) * 100}%`;
};

type Props = {
  onClose: () => void;
};

const CityInfo = ({ onClose: handleClose }: Props) => {
  const [locationName, setLocationName] = useState<string>("");

  const { data: weatherForecast } = useWeatherForecastQuery(locationName);

  const { data: hotelCount } = useHotelCountQuery(locationName);

  const { data: historicalMonumentCount } =
    useHistoricalMonumentCountQuery(locationName);

  const { data: hospitalCount } = useHospitalCountQuery(locationName);

  const { data: hospitalBedCount } = useHospitalBedCountQuery(locationName);

  const {
    data: populationData = {
      categories: [],
      population: [],
      males: [],
      females: [],
      households: [],
    },
  } = usePopulationQuery(locationName);

  var populationCount =
    populationData.population[populationData.population.length - 1];

  var householdsCount =
    populationData.households[populationData.households.length - 1];

  var maleCount = populationData.males[populationData.males.length - 1];

  var femaleCount = populationData.females[populationData.females.length - 1];

  const { data: landTrade } = useLandTradeQuery();

  const landTradeRank = useMemo(() => {
    const rank = landTrade?.slice(0, 10) ?? [];

    return rank.reduce(
      (acc, curr) => {
        acc.categories.push(curr.district);
        acc.data.push(curr.count);

        return acc;
      },
      { categories: [], data: [] }
    );
  }, [landTrade]);

  const landTradeCount = useMemo(
    () => landTrade?.reduce((prev, curr) => prev + curr.count, 0),
    [landTrade]
  );

  const { data: cityInfo } = useCityInfoQuery();

  const transportationCount = useMemo<{
    airline: number;
    trainStation: number;
    tBikeStop: number;
  }>(() => {
    if (!cityInfo) {
      return {
        airline: 0,
        trainStation: 0,
        tBikeStop: 0,
      };
    }

    if (locationName) {
      return {
        airline:
          cityInfo.交通.資料集[0].數據集.find((x) => x.行政區 === locationName)
            ?.數據 ?? 0,
        trainStation:
          cityInfo?.交通.資料集[1].數據集.find((x) => x.行政區 === locationName)
            ?.數據 ?? 0,
        tBikeStop:
          cityInfo?.交通.資料集[3].數據集.find((x) => x.行政區 === locationName)
            ?.數據 ?? 0,
      };
    }

    return {
      airline: cityInfo.交通.資料集[0].數據集.reduce(
        (prev, curr) => prev + curr.數據,
        0
      ),
      trainStation: cityInfo.交通.資料集[1].數據集.reduce(
        (prev, curr) => prev + curr.數據,
        0
      ),
      tBikeStop: cityInfo.交通.資料集[3].數據集.reduce(
        (prev, curr) => prev + curr.數據,
        0
      ),
    };
  }, [locationName, cityInfo]);

  const [showPopulationInfo, setShowPopulationInfo] = useState(false);

  const { data: cityInfoUpdateDate } = useForestageCityInfoUpdateDateQuery();

  return (
    <section
      className="fixed top-16 left-0 z-30 flex h-[100vh] w-[100vw] min-w-[375px] flex-col overflow-y-auto bg-grandma-400 bg-opacity-70 bg-[url('../images/bg-cityinfo.jpg')] bg-cover bg-bottom  bg-no-repeat md:top-0 lg:h-[100vh]"
      css={css`
        & .apexcharts-tooltip {
          background: #fff;
          color: black;
        }
      `}
    >
      <div className="order-1 mx-6 my-3 hidden items-center justify-between md:flex">
        <div className="flex items-center">
          <img
            src={Logo}
            className="hidden h-[43px] w-[222px] md:block md:h-[68px] md:w-[336px]"
            alt="台南城市儀表板"
            draggable="false"
          />
          <p className="ml-4 hidden border-l border-gray-700 border-opacity-20 pl-4 text-3xl font-bold md:block">
            台南城市資訊
          </p>
        </div>
        <div className="flex items-center">
          <div className="mr-10 hidden w-[170px] bg-gradient-to-l from-[#FFFFFF66] to-[#FAFAFA33] p-0.5 lg:block">
            <div className="relative bg-[#12b1b0] px-2 text-[20px]">
              <select
                className="h-[44px] w-full bg-transparent outline-none "
                css={css`
                  -webkit-appearance: none;
                  & > option {
                    color: black;
                  }
                `}
                onChange={({ target: { value } }) => setLocationName(value)}
              >
                <option value="">全部</option>
                {DISTRICT.map((x) => (
                  <option key={x} value={x}>
                    {x}
                  </option>
                ))}
              </select>
              <ChevronDown className="pointer-events-none absolute top-[10px]  right-2" />
            </div>
          </div>
          <img
            className="hidden h-9 w-9 cursor-pointer md:block"
            src={IconClose}
            alt="close"
            draggable="false"
            onClick={handleClose}
          />
        </div>
      </div>
      <div className="order-2 grow pb-12">
        <div className="grid grid-cols-1 gap-[30px] px-4 pt-[16px] md:gap-[88px] md:pl-[76px] md:pr-[100px] lg:grid-cols-3">
          <div>
            <div className="flex items-center">
              <div className="flex flex-auto items-center pb-2 lg:justify-center ">
                <div className="h-[74px] w-[74px] md:h-[118px] md:w-[118px]">
                  <img
                    className="w-full"
                    src={IconWeather}
                    alt="氣溫"
                    draggable="false"
                  />
                </div>

                <p className="ml-4 text-[48px] font-medium leading-9 tracking-tighter md:text-[65px]">
                  {weatherForecast ? weatherForecast.maxT : "-"}
                  <small className="pl-1 pt-1 align-top text-[48px] md:text-[65px]">
                    °
                  </small>
                </p>
              </div>
              <div className="w-[120px] bg-gradient-to-l from-[#FFFFFF66] to-[#FAFAFA33] p-0.5 lg:hidden">
                <div className="relative bg-[#12b1b0] px-2 text-[20px]">
                  <select
                    className="h-[44px] w-full bg-transparent outline-none"
                    css={css`
                      -webkit-appearance: none;
                      & > option {
                        color: black;
                      }
                    `}
                    onChange={({ target: { value } }) => setLocationName(value)}
                  >
                    <option value="">全部</option>
                    {DISTRICT.map((x) => (
                      <option key={x} value={x}>
                        {x}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="pointer-events-none absolute top-[10px] right-2" />
                </div>
              </div>
            </div>

            <article>
              <div>
                <CityInfoUnitTitle
                  name="人口"
                  needInfo
                  onClickInfo={() => setShowPopulationInfo(true)}
                />
                <div className="grid grid-cols-2 gap-5">
                  <CityInfoUnit
                    name="在籍人口"
                    summary={populationCount}
                    unit="人"
                  />
                  <CityInfoUnit
                    name="登記戶數"
                    summary={householdsCount}
                    unit="戶"
                  />
                </div>
              </div>
              <div>
                <div className="flex w-full py-6">
                  <div
                    className="bg-[#3CC35A] p-2 font-extrabold"
                    css={css`
                      flex: ${getPercentString(maleCount, populationCount)} 0 0;
                    `}
                  >
                    男性 {maleCount?.toLocaleString() ?? "-"}
                  </div>
                  <div
                    className="bg-[#ED8D8D] p-2 text-right font-extrabold"
                    css={css`
                      flex: ${getPercentString(femaleCount, populationCount)} 0
                        0;
                    `}
                  >
                    {femaleCount?.toLocaleString() ?? "-"} 女性
                  </div>
                </div>
              </div>
              <div>
                <LineChart
                  height={240}
                  categories={populationData.categories}
                  data={populationData.population}
                  seriesName="在籍人口"
                />

                <div className="pt-2 text-right text-xs text-[#D3D3D3]">
                  資料來源&nbsp;
                  {cityInfo?.人口 && (
                    <a
                      href={cityInfo.人口.網址}
                      target="_blank"
                      rel="noreferrer"
                      className="text-[#CBBC87]"
                    >
                      {cityInfo.人口.資料來源}
                    </a>
                  )}
                </div>
              </div>
            </article>
          </div>
          <div>
            <div>
              <CityInfoUnitTitle name="土地" />

              <div className="flex justify-between gap-5 pb-3">
                <CityInfoUnit
                  name="不動產本年成交"
                  summary={landTradeCount}
                  unit="筆"
                />
              </div>

              <BarChart
                title="行政區交易量排名"
                height={240}
                categories={landTradeRank.categories}
                data={landTradeRank.data}
                seriesName="行政區交易量"
              />

              <div className="pt-2 text-right text-xs text-[#D3D3D3]">
                資料來源&nbsp;
                {cityInfo?.土地 && (
                  <a
                    href={cityInfo.土地.網址}
                    target="_blank"
                    rel="noreferrer"
                    className="text-[#CBBC87]"
                  >
                    {cityInfo.土地.資料來源}
                  </a>
                )}
              </div>
            </div>

            <div>
              <CityInfoUnitTitle name="綠能" />

              <div className="flex justify-between gap-5">
                <CityInfoUnit
                  name={cityInfo?.綠能.資料.文字}
                  summary={cityInfo?.綠能.資料.數據}
                  unit={cityInfo?.綠能.資料.數據單位}
                />
              </div>
              <div className="pt-2 text-right text-xs text-[#D3D3D3]">
                資料來源&nbsp;
                {cityInfo?.綠能 && (
                  <a
                    href={cityInfo.綠能.網址}
                    target="_blank"
                    rel="noreferrer"
                    className="text-[#CBBC87]"
                  >
                    {cityInfo.綠能.資料來源}
                  </a>
                )}
              </div>
            </div>
          </div>
          <div>
            <div>
              <CityInfoUnitTitle name="觀光" />

              <div className="grid grid-cols-2 gap-5">
                <CityInfoUnit name="旅館民宿" summary={hotelCount} unit="間" />
                <CityInfoUnit
                  name="文化古蹟"
                  summary={historicalMonumentCount}
                  unit="間"
                />
              </div>
              <div className="pt-2 text-right text-xs text-[#D3D3D3]">
                資料來源&nbsp;
                {cityInfo?.觀光 && (
                  <a
                    href={cityInfo.觀光.網址}
                    target="_blank"
                    rel="noreferrer"
                    className="text-[#CBBC87]"
                  >
                    {cityInfo.觀光.資料來源}
                  </a>
                )}
              </div>
            </div>

            <div>
              <CityInfoUnitTitle name="醫療" />

              <div className="grid grid-cols-2 gap-5">
                <CityInfoUnit
                  name="醫院/診所"
                  summary={hospitalCount}
                  unit="間"
                />
                <CityInfoUnit
                  name="病床數"
                  summary={hospitalBedCount}
                  unit="床"
                />
              </div>
              <div className="pt-2 text-right text-xs text-[#D3D3D3]">
                資料來源&nbsp;
                {cityInfo?.醫療 && (
                  <a
                    href={cityInfo.醫療.網址}
                    target="_blank"
                    rel="noreferrer"
                    className="text-[#CBBC87]"
                  >
                    {cityInfo.醫療.資料來源}
                  </a>
                )}
              </div>
            </div>

            <div className="pb-24 md:pb-0">
              <CityInfoUnitTitle name="交通" />

              <div className="grid grid-cols-2 gap-5">
                {/* 飛機場 */}
                <CityInfoUnit
                  name={cityInfo?.交通.資料集[0].文字}
                  summary={transportationCount.airline}
                  unit={cityInfo?.交通.資料集[0].數據單位}
                  url={cityInfo?.交通.資料集[0].網址}
                />

                {/* 火車站 */}
                <CityInfoUnit
                  name={cityInfo?.交通.資料集[1].文字}
                  summary={transportationCount.trainStation}
                  unit={cityInfo?.交通.資料集[1].數據單位}
                  url={cityInfo?.交通.資料集[1].網址}
                />

                {/* 公車路線 */}
                <CityInfoUnit
                  name={cityInfo?.交通.資料集[2].文字}
                  summary={cityInfo?.交通.資料集[2].數據}
                  unit={cityInfo?.交通.資料集[2].數據單位}
                  url={cityInfo?.交通.資料集[2].網址}
                />

                {/* T-Bike 車站 */}
                <CityInfoUnit
                  name={cityInfo?.交通.資料集[3].文字}
                  summary={transportationCount.tBikeStop}
                  unit={cityInfo?.交通.資料集[3].數據單位}
                  url={cityInfo?.交通.資料集[3].網址}
                />
              </div>

              {/* { cityInfo && cityInfo.交通.資料集 && (
                  <div>
                    { cityInfo.交通.資料集.filter((n) => n.文字 == "火車站").map((n)=> (
                      <li>
                        {n.數據集.map((m)=> m.行政區)}
                      </li>
                    )
                    ) }
                  </div>
                )} */}

              <div className="pt-2 text-right text-xs text-[#D3D3D3]">
                資料來源&nbsp;
                {cityInfo?.交通 && (
                  <a
                    href={cityInfo.交通.網址}
                    target="_blank"
                    rel="noreferrer"
                    className="text-[#CBBC87]"
                  >
                    {cityInfo.交通.資料來源}
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="order-1 flex justify-between px-4 pt-4 pb-4 text-[14px] md:order-3 md:pt-0">
        <div className="flex items-center">
          <Link to="/" className="mr-1" onClick={() => handleClose()}>
            <svg
              width="11"
              height="13"
              viewBox="0 0 11 13"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M9.91653 3.78696L6.41653 0.480499C5.75262 -0.160166 4.74745 -0.160166 4.08357 0.480499L0.58357 3.78696C0.208413 4.14825 -0.00377124 4.66632 5.07565e-05 5.20798V10.7028C5.07565e-05 11.7443 0.783728 12.5889 1.75005 12.5889H8.75005C9.71637 12.5889 10.5 11.7443 10.5 10.7028V5.20149C10.5022 4.6622 10.29 4.14707 9.91653 3.78696V3.78696ZM6.41653 11.3311H4.08357V8.18792C4.08357 7.84077 4.34443 7.55904 4.66654 7.55904H5.83357C5.98834 7.55904 6.13654 7.62564 6.24591 7.74353C6.35528 7.86141 6.41654 8.02112 6.41654 8.18792L6.41653 11.3311ZM9.33301 10.7022L9.33355 10.7028C9.33355 10.8696 9.27175 11.0294 9.16237 11.1472C9.05299 11.2651 8.9048 11.3311 8.75003 11.3311H7.58355V8.18793C7.58355 7.14648 6.79988 6.30188 5.83355 6.30188H4.66653C3.70021 6.30188 2.91653 7.14648 2.91653 8.18793V11.3311H1.75005C1.42794 11.3311 1.16653 11.05 1.16653 10.7028V5.20148C1.16708 5.02112 1.23927 4.84962 1.36505 4.72996L4.86505 1.42939C5.08544 1.22134 5.41466 1.22134 5.63505 1.42939L9.13505 4.72996C9.26083 4.84961 9.33302 5.02112 9.33357 5.20148L9.33301 10.7022Z"
                fill="white"
              />
            </svg>
          </Link>
          {">"} 台南城市資訊
        </div>
        <div>{cityInfoUpdateDate} 更新</div>
      </div>

      {showPopulationInfo && (
        <div className="top-0 left-0 z-30 hidden h-[100vh] w-[100vw] bg-[#064646ab] md:fixed md:block">
          <div className="my-0 mx-auto md:mt-[64px] md:block md:max-h-[660px] md:max-w-[960px]">
            <h2 className="mb-4 flex content-between text-3xl font-medium text-white">
              <span className="grow">人口趨勢資訊</span>
              <img
                className="mr-3 w-9 cursor-pointer"
                src={IconInfoClose}
                alt="close"
                draggable="false"
                onClick={() => {
                  setShowPopulationInfo(false);
                }}
              />
            </h2>
            <div className="grid grid-cols-2 gap-4 rounded-2xl bg-[#007E80] px-12 py-8">
              <div>
                <h3 className="my-2 text-lg">男性人口趨勢圖</h3>
                <LineChart
                  height={240}
                  categories={populationData.categories}
                  data={populationData.males}
                  seriesName="男性人口趨勢圖"
                />
              </div>

              <div>
                <h3 className="my-2 text-lg">女性人口趨勢圖</h3>
                <LineChart
                  height={240}
                  categories={populationData.categories}
                  data={populationData.females}
                  seriesName="女性人口趨勢圖"
                />
              </div>

              <div>
                <h3 className="my-2 text-lg">登記人口趨勢圖</h3>
                <LineChart
                  height={240}
                  categories={populationData.categories}
                  data={populationData.population}
                  seriesName="登記人口趨勢圖"
                />
              </div>

              <div>
                <h3 className="my-2 text-lg">登記戶數趨勢圖</h3>

                <LineChart
                  height={240}
                  categories={populationData.categories}
                  data={populationData.households}
                  seriesName="登記戶數趨勢圖"
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};
export default CityInfo;
